import { CircleAlert, CircleCheckBig, Info, type LucideIcon } from 'lucide-react';

import { Typography } from '@/components/ui/typography';
import type { ConnectionState } from '../../pms-configuration/types/connection-state';
import type { IntegrationStatus } from '../../services/pmsConnectionApi';
import { integrationStatusCopy as copy } from './copy';

interface Props { status: IntegrationStatus; connectionState: ConnectionState }

const Label = ({ icon: Icon, tone, children }: { icon: LucideIcon; tone: string; children: string }) => (
  <span className={`inline-flex items-center gap-2 ${tone}`}>
    <Icon className="size-4.5 shrink-0" aria-hidden="true" />
    <Typography variant="body-medium" className={tone}>{children}</Typography>
  </span>
);

/**
 * Integration-level status for the vendor page header ("Integration is
 * Active" · icon + text, per the Integrations Redesign prototype).
 * "Disabled" is a deliberate pause, "Inactive" is a failed credential test.
 * Renders nothing when the feature is unavailable (flag off) or nothing is
 * connected — the pre-DV-2549 header, unchanged — except for a failed
 * credential test, which is reported in every status (see below).
 */
export const IntegrationStatusLabel = ({ status, connectionState }: Props) => {
  // A failed credential test is reported first, ahead of the integration-status
  // branches. The retired VendorConnectionHeader rendered "Connection failed."
  // from `connectionState` alone, with no knowledge of the feature flag, so
  // gating this on `status` would drop the only failure signal Rentvine and
  // AppFolio have when the flag is off (`unavailable`) or nothing is stored
  // yet (`none`) — the pre-DV-2549 parity pmsConnectionApi's contract requires.
  if (connectionState.kind === 'failed') {
    return <Label icon={CircleAlert} tone="text-destructive">{copy.header.inactive}</Label>;
  }
  if (status.kind === 'none' || status.kind === 'unavailable') return null;
  if (status.kind === 'disabled') {
    return <Label icon={Info} tone="text-muted-foreground">{copy.header.disabled}</Label>;
  }
  return <Label icon={CircleCheckBig} tone="text-success">{copy.header.active}</Label>;
};
